import clsx from 'clsx'
import React, { PropsWithChildren, useCallback } from 'react'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineCalendarAlertIcon from 'LuxKit/Icons/line/LineCalendarAlertIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineCalendarPlusIcon from 'LuxKit/Icons/line/LineCalendarPlusIcon'
import Tooltip from 'LuxKit/Tooltip'
import noop from 'lib/function/noop'
import { preventDefault, stopPropagation } from 'lib/web/eventHandlerUtils'

const ItineraryChip = styled(AssistChip)`
  &:not(.in-itinerary) {
    gap: 0;
  }
`

interface Props {
  label?: string
  onClick?: () => void
  itineraryDate?: moment.Moment
  isProcessing?: boolean
  variant: 'chip' | 'button'
  hasWarning?: boolean
}

/**
 * For bookmarking something and adding to the itinerary at the same time
 */
const BaseBookmarkToItineraryButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>((props, ref) => {
  const {
    onClick = noop,
    itineraryDate,
    isProcessing = false,
    variant,
    hasWarning = false,
  } = props

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      preventDefault(e)
      stopPropagation(e)

      onClick()
    },
    [onClick],
  )

  let startIcon
  if (hasWarning) {
    startIcon = <LineCalendarAlertIcon colour="warning" />
  } else if (itineraryDate) {
    startIcon = <LineCalendarCheckIcon />
  } else {
    startIcon = <LineCalendarPlusIcon />
  }

  return variant === 'button' ? (
    <TextButton
      ref={ref}
      aria-label={itineraryDate ? 'Remove from itinerary' : 'Add to itinerary'}
      kind="tertiary"
      onClick={handleClick}
      disabled={isProcessing}
      size="large"
      startIcon={startIcon}
    >
      Move to
    </TextButton>
  ) : (
    <Tooltip
      description={itineraryDate ? 'Move to another day' : 'Add to itinerary'}
    >
      <ItineraryChip
        ref={ref}
        aria-label={
          itineraryDate ? 'Move to another day' : 'Add to itinerary'
        }
        className={clsx({ 'in-itinerary': !!itineraryDate })}
        variant="filled"
        size="small"
        onClick={handleClick}
        startIcon={startIcon}
        disabled={isProcessing}
      />
    </Tooltip>
  )
})

BaseBookmarkToItineraryButton.displayName = 'BaseBookmarkToItineraryButton'

export default BaseBookmarkToItineraryButton
